import { FC, forwardRef } from 'react';
import { DialogProps } from '@toolpad/core/useDialogs';
import reactStringReplace from 'react-string-replace';
import { useDialogs } from '@toolpad/core/useDialogs';
import {
  Dialog,
  DialogContent,
  Zoom,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation, Trans } from 'react-i18next';

import { ViewNavigationBar, ConfirmDialog } from '@components';
import { useGetOrder, useCancelOrder } from '../hooks';
import { TinPreview } from '@core/features/artworks';

import { DoneIcon } from '@ui/icons';
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Zoom
      // style={{ transformOrigin: '0 0 0' }}
      // easing={{
      //   enter: 'cubic-bezier(0.66, 0, 0.34, 1)',
      //   exit: 'linear',
      // }}
      ref={ref}
      {...props}
    />
  );
});

export const OrderDetailDialog: FC<DialogProps<string>> = ({
  payload,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data: order, isPending } = useGetOrder(payload, {
    enabled: open,
    refetchOnWindowFocus: false,
  });
  const { t } = useTranslation();
  const dialogs = useDialogs();

  const { mutate: cancelOrder } = useCancelOrder();

  const handleCancelOrder = async () => {
    const closeStatus = await dialogs.open(ConfirmDialog, {
      okText: t('confirm'),
      cancelText: t('cancel'),
      text: t('cancelOrderConfirmationText'),
    });

    if (!order) return;
    if (closeStatus === 'confirmed') {
      cancelOrder(order.id);
      onClose();
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullWidth={true}
        maxWidth="lg"
        fullScreen={fullScreen}
        scroll="paper"
        slots={{
          transition: Transition,
        }}
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
      >
        <ViewNavigationBar
          onClose={() => onClose()}
          closeTitle={t('close')}
          title={t('yourOrder')}
        />
        <DialogContent
          sx={{
            position: 'relative',
            height: '100%',
            minHeight: {
              md: 'calc(100vh - 200px)',
            },
            padding: 0,
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              height: '100%',
            }}
          >
            {isPending ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Stack
                width={'100%'}
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={6}
                sx={{ paddingTop: 10, paddingBottom: 30 }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="center"
                  gap={4}
                  sx={{ width: '100%' }}
                >
                  {order?.positions.map((position) => (
                    <Box
                      key={position.artwork.id}
                      sx={{
                        width: 250,
                        height: 250,
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      <TinPreview image={position.artwork.thumbnail ?? ''} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 18,
                          right: 18,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            paddingTop: 2,
                            paddingBottom: 2,
                            paddingLeft: 3,
                            paddingRight: 3,
                            backgroundColor: '#FF7A00',
                            borderRadius: 500,
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexBasis: 'auto',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: '#FFFFFF',
                            }}
                          >
                            {position.quantity} x
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            paddingTop: 2,
                            paddingBottom: 2,
                            paddingLeft: 4,
                            paddingRight: 4,
                            backgroundColor: '#00A39C',
                            borderRadius: 500,
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexBasis: 'auto',
                          }}
                        >
                          <DoneIcon
                            sx={{
                              width: 16,
                              height: 16,
                              color: '#FFFFFF',
                              marginRight: 2,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: '#FFFFFF',
                            }}
                          >
                            {t('readyForPickup')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                    {t('orderToBePlacedAt')}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 400 }}>
                    {reactStringReplace(
                      order?.venue,
                      'Nivea Haus',
                      (match, i) => (
                        <Box
                          component="b"
                          key={i}
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                          }}
                        >
                          {match}
                        </Box>
                      )
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 400 }}>
                    {order
                      ? new Date(order.created_at).toLocaleDateString()
                      : ''}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid #0032A0',
                    height: 69,
                    borderRadius: 500,
                    paddingTop: 3,
                    paddingBottom: 3,
                    paddingLeft: 10,
                    paddingRight: 10,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 45,
                      fontWeight: 400,
                      fontFamily: 'monospace',
                    }}
                  >
                    {order?.squid ?? ''}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 200,
                    height: 200,
                  }}
                  component={'img'}
                  src={order?.qrcode_image ?? undefined}
                />

                <Box sx={{ maxWidth: 300 }}>
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 400, textAlign: 'center' }}
                  >
                    <Trans i18nKey="showThisCodeAtThePickupStation" />
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  sx={{
                    color: '#B90A2C',
                  }}
                  onClick={handleCancelOrder}
                >
                  {t('cancelOrder')}
                </Button>
              </Stack>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
